import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GlobeIcon, PushPinIcon } from '@phosphor-icons/react';
import DocumentBrowser from 'src/components/DocumentBrowser';
import WikiFilterDropdown from 'src/features/wiki/components/WikiFilterDropdown';
import { useAggregations } from 'src/features/database/hooks/useAggregations';
import { useFilterOptions, FilterOption } from 'src/shared/filters/databaseFilterOptions';
import { ChatSearchFilters, FilterType } from '../types';

interface AllFiltersPanelProps {
  chatFilters: ChatSearchFilters;
  onFilterChange: (type: FilterType, newValues: string[]) => void;
  onClearAll: () => void;
  /**
   * Optional: when provided, renders the given text instead of the default
   * "CompliMind is searching N documents" footer. Receives the effective
   * document count (either the count of explicitly-selected documents, or the
   * total matching the current filters if none are explicitly selected).
   */
  documentCountLabel?: (count: number) => React.ReactNode;
  /**
   * When true, disables all interaction. Dropdowns still open so the user can
   * see the selected values, but checkboxes and the document browser become
   * inert and the "Clear all filters" action is hidden.
   */
  readOnly?: boolean;
}

const AllFiltersPanel: React.FC<AllFiltersPanelProps> = ({
  chatFilters,
  onFilterChange,
  onClearAll,
  documentCountLabel,
  readOnly = false
}) => {
  // Fetch aggregations scoped to the current hierarchical selection so the
  // dropdown counts stay in sync as the user narrows the filter.
  const { data: aggregationsData } = useAggregations({
    categories: chatFilters.category,
    domains: chatFilters.domain,
    subdomains: chatFilters.subDomain,
    documentIds: chatFilters.document
  });
  const filterOptions = useFilterOptions(aggregationsData);

  const domainOptions = useMemo(
    () =>
      filterOptions.domains.map((opt: FilterOption) => ({
        label: opt.label,
        value: opt.value,
        count: String(opt.count),
        disabled: opt.disabled
      })),
    [filterOptions.domains]
  );

  const subDomainOptions = useMemo(
    () =>
      filterOptions.subDomains.map((opt: FilterOption) => ({
        label: opt.label,
        value: opt.value,
        count: String(opt.count),
        disabled: opt.disabled
      })),
    [filterOptions.subDomains]
  );

  // Pass current hierarchical selection to DocumentBrowser so its paginated
  // list only shows documents that match the active filters.
  const browserFilters = useMemo(
    () => ({
      category: chatFilters.category,
      domain: chatFilters.domain,
      subDomain: chatFilters.subDomain
    }),
    [chatFilters.category, chatFilters.domain, chatFilters.subDomain]
  );

  const browserFilterKeyToChatType: Record<string, FilterType> = {
    category: 'category',
    domain: 'domain',
    subDomain: 'subDomain'
  };

  const handleBrowserFilterChange = (key: string, values: string[]) => {
    if (readOnly) return;
    const type = browserFilterKeyToChatType[key];
    if (!type) return;
    onFilterChange(type, values);
  };

  const handleSelectionChange = (items: (string | number)[]) => {
    if (readOnly) return;
    onFilterChange(
      'document',
      items.map((id) => id.toString())
    );
  };

  const domainLabel = chatFilters.domain.length > 0 ? `Domains (${chatFilters.domain.length})` : 'Domains';
  const subDomainLabel =
    chatFilters.subDomain.length > 0 ? `Sub domains (${chatFilters.subDomain.length})` : 'Sub domains';

  const totalSelectedFilters = chatFilters.domain.length + chatFilters.subDomain.length + chatFilters.document.length;
  const hasActiveFilters = totalSelectedFilters > 0;

  const [totalDocumentCount, setTotalDocumentCount] = useState<number | null>(null);

  const handleClearAll = () => {
    if (readOnly) return;
    onClearAll();
  };

  // Dynamically size the document table's page length to fill the vertical
  // space the browser has available. The observer subtracts the fixed overhead
  // of the DocumentBrowser chrome (search row, tag row, pagination, gaps) and
  // divides by an approximate row height. Values below are empirical; they
  // just need to keep the pagination pinned at the bottom of the panel
  // without triggering an internal scroll.
  const browserWrapperRef = useRef<HTMLDivElement>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const el = browserWrapperRef.current;
    if (!el) return;

    // Search header (~90) + table thead (~44) + table→pagination gap (16) +
    // pagination (~48) + a small safety buffer to guarantee we never pick a
    // row count that would trigger an internal scroll.
    const BROWSER_OVERHEAD_PX = 220;
    const ROW_HEIGHT_PX = 48;
    const MIN_ROWS = 3;
    const MAX_ROWS = 25;

    const recompute = () => {
      const available = el.clientHeight - BROWSER_OVERHEAD_PX;
      const rows = Math.floor(available / ROW_HEIGHT_PX);
      const clamped = Math.min(MAX_ROWS, Math.max(MIN_ROWS, rows));
      setItemsPerPage((prev) => (prev === clamped ? prev : clamped));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const effectiveDocCount = chatFilters.document.length > 0 ? chatFilters.document.length : (totalDocumentCount ?? 0);
  const footerLabel = documentCountLabel
    ? documentCountLabel(effectiveDocCount)
    : `CompliMind is searching ${effectiveDocCount} ${effectiveDocCount === 1 ? 'document' : 'documents'}`;

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start flex-shrink-0">
        <WikiFilterDropdown
          options={domainOptions}
          value={chatFilters.domain}
          onChange={(newSelected) => onFilterChange('domain', newSelected)}
          label={domainLabel}
          icon={<GlobeIcon size={18} weight="light" />}
          placeholder="Search domains..."
          searchable={true}
          initialOpen={false}
          overlay={true}
          disabled={readOnly}
        />
        <WikiFilterDropdown
          options={subDomainOptions}
          value={chatFilters.subDomain}
          onChange={(newSelected) => onFilterChange('subDomain', newSelected)}
          label={subDomainLabel}
          icon={<PushPinIcon size={18} weight="light" />}
          placeholder="Search sub domains..."
          searchable={true}
          initialOpen={false}
          overlay={true}
          disabled={readOnly}
        />
      </div>

      <div ref={browserWrapperRef} className="flex-1 min-h-0 overflow-hidden flex">
        <DocumentBrowser
          mode="browse"
          hideFilterPanel={true}
          filterKeys={['domain', 'subDomain']}
          filters={browserFilters}
          onFilterChange={handleBrowserFilterChange}
          selectedItems={chatFilters.document}
          onSelectionChange={handleSelectionChange}
          selectionMode={readOnly ? 'none' : 'toggle'}
          hideCategoryColumn={true}
          hideDomainColumn={true}
          hideSubDomainColumn={true}
          hidePublishedColumn={true}
          hideLikeAction={true}
          hideLikedDocumentsFilter={true}
          enableSorting={false}
          itemsPerPage={itemsPerPage}
          tableMinHeight={0}
          disableInternalScroll={true}
          hideDocumentCount={true}
          onTotalCountChange={setTotalDocumentCount}
        />
      </div>

      <div className="flex items-center justify-between flex-shrink-0 pt-3 border-t gap-6">
        <span className="text-sm text-content-default font-semibold whitespace-nowrap">{footerLabel}</span>
        {hasActiveFilters && !readOnly && (
          <button
            type="button"
            className="text-content-subtle text-xs underline hover:text-primary-blue-dark font-medium whitespace-nowrap"
            onClick={handleClearAll}
          >
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
};

export default AllFiltersPanel;
